import { useMemo } from 'react';
import { describeZoneLabel, knownTimeZones } from '@simpleacs/shared';
import { SearchSelect } from './ui';

/**
 * Which zone a chassis is in, chosen from what this runtime actually knows.
 *
 * The list comes from `Intl` rather than from a table kept here, for the same
 * reason the offset does: the tzdata the process holds is the only authority
 * on which names it can convert, and a name the form offers but the server
 * cannot convert would render a chassis command around a guessed offset.
 *
 * Every row carries its current UTC offset, because "Africa/Nairobi" and
 * "Africa/Kampala" are the same clock and an operator picking between them is
 * choosing a label, not a time — while "America/Phoenix" and "America/Denver"
 * look interchangeable and are not.
 */
export function TimezoneSelect({
  value,
  onChange,
  emptyLabel = 'No timezone',
}: {
  value: string | null;
  onChange: (zone: string | null) => void;
  emptyLabel?: string;
}) {
  const options = useMemo(() => {
    const now = Date.now();
    return knownTimeZones().map((zone) => ({
      value: zone,
      label: zone,
      hint: describeZoneLabel(zone, now),
    }));
  }, []);

  return (
    <SearchSelect
      value={value ?? ''}
      options={options}
      onChange={(next) => onChange(next || null)}
      placeholder="Select a timezone…"
      emptyLabel={emptyLabel}
      visibleRows={7}
    />
  );
}
